import { any, compose, equals, includes, isNil, join, map, memoizeWith, path, pathOr, pluck, toPairs } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createSelector } from 'reselect';

import { getCduUserPermissions } from '@gef-ui/cdu-user/selectors';

import { getContextuserPermissions } from 'features/views/userContext/selectors';

const memoizeWithPermissionValueList = memoizeWith(
	compose(
		join('|'),
		map(({ id, attributes = [] }) =>
			join('-')([
				id,
				...pluck('key', attributes),
				map(compose(join('='), map(toPairs)))(pluck('value', attributes)),
			]),
		),
	),
);

const includeValue =
	(permissions, id) =>
	({ key, value }) => {
		if (!permissions.hasOwnProperty(id)) {
			return false;
		}

		const permission = permissions[id]?.values;

		if (isNilOrEmpty(permission) && !permission.hasOwnProperty(key)) {
			return false;
		}

		const permissionToKeysList = map((attrValue) => {
			const attrValues = permission[key];
			return includes(attrValue, attrValues);
		})(value);

		return any(equals(true))(permissionToKeysList);
	};

/**
 * @example
 {
    id: 101,
	attributes: [{
		key: 'enabled_search_fields',
		value: ['a', 'b'],
	}],
 },
 */
export const hasPermissionByIdsAndIncludedValues = createSelector(getCduUserPermissions, (permissions) =>
	memoizeWithPermissionValueList(any(({ id, attributes = [] }) => any(includeValue(permissions, id), attributes))),
);

// same logic as selector under but for context user permissions
export const hasContextUserPermissionByIdsAndIncludedValues = createSelector(getContextuserPermissions, (permissions) =>
	memoizeWithPermissionValueList(
		any(({ id, attributes = [] }) => any(includeValuesListOrContainsAllKeyword(permissions, id), attributes)),
	),
);

export const hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck = createSelector(
	getCduUserPermissions,
	(permissions) =>
		memoizeWithPermissionValueList(
			any(({ id, attributes = [] }) => any(includeValuesListOrContainsAllKeyword(permissions, id), attributes)),
		),
);

// it either matches with specified aclValue or with "all" keyword
const checkAclValue = (attr, value) => {
	return attr === 'all' || includes(attr, value);
};

export const includeValuesListOrContainsAllKeyword =
	(permissions, id) =>
	({ key, value }) => {
		// permission ID doesnt exist
		if (!permissions.hasOwnProperty(id)) {
			return false;
		}

		const permission = permissions[id]?.values;
		// permission doesnt have "values" nor the required key
		if (isNilOrEmpty(permission) && !permission.hasOwnProperty(key)) {
			return false;
		}

		let attr;

		// checking if permission contains the "all/All" keyword

		// check if "list" attr exists
		const hasListAttribute = path([id, 'values', key, 0, 'list'], permissions);

		// if list doesnt exists, grab the main value (could be "all" value in array)
		if (isNil(hasListAttribute)) {
			attr = pathOr([], [id, 'values', key], permissions);
		} else {
			attr = hasListAttribute;
		}

		if (!attr) {
			return false;
		} else {
			return attr.map((a) => checkAclValue(a, value)).includes(true);
		}
	};
